import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import useToggle from 'hooks/useToggle'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Clickable from 'components/Common/Clickable'
import styled from 'styled-components'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import { rem } from 'polished'

const RATIO_TEXT_SIZE_CONTAINER_LENGTH = 4

const StyledLink = styled(TextLink)`
  padding-left: ${rem(8)};
`

interface Props {
  prefix?: string
  locationsVisited: Array<string>
  showAllLocations?: boolean
}

function TourV2ItineraryLocationList(props: Props) {
  const intl = useIntl()
  const { prefix = '', locationsVisited, showAllLocations = false } = props

  const [maxLengthDestination, setMaxLengthDestination] = useState<number | null>(null)

  const { value: isReadMoreToggled, toggle: toggleReadMore } = useToggle(false)
  const wrapper = useRef<HTMLDivElement | null>(null)

  const locationsVisitedText = useMemo(() => {
    if (locationsVisited.length === 0 || showAllLocations) {
      const text = locationsVisited.length === 0 ? '' : prefix.concat(locationsVisited.join(', '))
      return { text, more: 0, isTextTruncated: false }
    }
    const fullLocationsVisitedText = prefix.concat(locationsVisited.join(', '))
    const locationsVisitedMaxLength = (maxLengthDestination ? (maxLengthDestination / RATIO_TEXT_SIZE_CONTAINER_LENGTH) : 100)
    // If it's longer than allowed, we got to truncate it, but also show how many more locations there are after the truncation
    if (fullLocationsVisitedText.length > locationsVisitedMaxLength) {
      const locationsVisitedText = fullLocationsVisitedText.substring(0, locationsVisitedMaxLength)
      const locationsVisitedCount = locationsVisited.length - locationsVisitedText.split(', ').length + 1
      if (!isReadMoreToggled) {
        return { text: `${locationsVisitedText}...`, more: locationsVisitedCount, isTextTruncated: true }
      }
      return { text: fullLocationsVisitedText, more: 0, isTextTruncated: true }
    }
    return { text: fullLocationsVisitedText, more: 0, isTextTruncated: false }
  }, [isReadMoreToggled, locationsVisited, prefix, maxLengthDestination, showAllLocations])

  useEffect(() => {
    if (wrapper.current) {
      setMaxLengthDestination(wrapper.current.offsetWidth)
    }
  }, [setMaxLengthDestination, wrapper])

  const showMoreText = intl.formatMessage(
    {
      id: 'R+KCP3t600',
      defaultMessage: '+{count} more',
      description: 'Link text showing number of additional locations not displayed in truncated list',
    },
    { count: locationsVisitedText.more },
  )
  const showLessText = intl.formatMessage({
    id: 'AXTie+bnlg',
    defaultMessage: 'show less',
    description: 'Link text to collapse the expanded locations list',
  })
  const toggleLinkText = !isReadMoreToggled ? showMoreText : showLessText

  return <div ref={wrapper}>
    {locationsVisited.length > 0 && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineMapMarkerIcon />}>
      <p>{locationsVisitedText.text}
        {locationsVisitedText.isTextTruncated &&
          <Clickable onClick={toggleReadMore}>
            <StyledLink size="caption">{toggleLinkText}</StyledLink>
          </Clickable>}
      </p>
    </BodyTextBlock>}
  </div>
}

export default TourV2ItineraryLocationList
